import { useState, useEffect, useRef } from "react";
import Icon1 from "@assets/icons/Icon";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { PDFViewerModal } from "@components/modals/PDFViewerModal";
import { Project, ProjectBillingStep } from "types";
import { projectsApi } from "@services/api";
import { showNotification } from "context/notification.service";

const BASE_STATUS: Record<string, { label: string; dot: string }> = {
  pending: { label: "En attente", dot: "bg-neutral-300" },
  creating: { label: "Création et envoi en cours...", dot: "bg-amber-400 animate-pulse" },
  draft: { label: "Brouillon", dot: "bg-amber-400" },
  finalized: { label: "Finalisée — envoi en attente", dot: "bg-amber-500" },
  sent: { label: "Envoyée", dot: "bg-green-500" },
  paid: { label: "Payée", dot: "bg-emerald-500" },
  error: { label: "Erreur", dot: "bg-red-500" },
};

function formatError(e: any): string {
  const d = e?.response?.data?.detail;
  if (!d) return "Une erreur est survenue";
  if (typeof d === "string") return d;
  return "Une erreur est survenue";
}

export function BillingTab({
  project,
  setProject,
  isGuest = false,
  isAdmin = false,
  canResend = false,
}: {
  project: Project;
  setProject: (p: Project) => void;
  isGuest?: boolean;
  isAdmin?: boolean;
  canResend?: boolean;
}) {
  const billing = project.billing;
  const [loading, setLoading] = useState(false);
  const [sendingKey, setSendingKey] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set());
  const [pdfModal, setPdfModal] = useState<{ url: string; title: string } | null>(null);
  const hasPdf = (step: ProjectBillingStep) => !!(step.invoice_pdf_url && step.invoice_pdf_url.startsWith("http"));
  const openPdf = (url: string, title: string) => setPdfModal({ url, title });

  // Days until project end (from cached planning_end_date on billing)
  const daysUntilEnd = billing?.planning_end_date
    ? Math.round((new Date(billing.planning_end_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  // Track steps that need polling: creating or finalized with invoice (send failed, needs retry)
  const hasCreating = billing?.steps?.some((s) => s.status === "creating") ?? false;
  const hasStuckFinalized = billing?.steps?.some((s) => s.status === "finalized" && s.invoice_id) ?? false;
  const needsPolling = hasCreating || hasStuckFinalized;

  // Retry state
  const retryCountRef = useRef<Record<number, number>>({});
  const MAX_RETRIES = 4;
  const RETRY_INTERVAL = 15; // seconds between sync retries

  // Countdown for UI
  const [retryCountdown, setRetryCountdown] = useState<number>(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }

    if (!needsPolling) {
      retryCountRef.current = {};
      setRetryCountdown(0);
      return;
    }

    const poll = async () => {
      try {
        if (hasStuckFinalized) {
          // Sync triggers auto-retry of sending finalized invoices on the backend
          const updated = await projectsApi.syncBilling(project.id);
          setProject(updated);
          // Track retry counts for finalized steps
          billing?.steps?.forEach((s, i) => {
            if (s.status === "finalized" && s.invoice_id) {
              retryCountRef.current[i] = (retryCountRef.current[i] ?? 0) + 1;
            }
          });
          // Clean up steps that resolved
          updated.billing?.steps?.forEach((s, i) => {
            if (s.status === "sent" || s.status === "paid") delete retryCountRef.current[i];
          });
        } else {
          // Just poll for creating → finalized/sent transition
          const updated = await projectsApi.get(project.id);
          setProject(updated);
          // Clear the "Envoi en cours..." visual once no step is creating.
          // setSendingKey(null) is idempotent — safe to call even if
          // sendingKey is already null (no unnecessary re-render).
          const stillCreating = updated.billing?.steps?.some(
            (s) => s.status === "creating",
          );
          if (!stillCreating) {
            setSendingKey(null);
          }
        }
      } catch {}
    };

    const interval = hasCreating ? 3000 : RETRY_INTERVAL * 1000;
    pollRef.current = setInterval(poll, interval);

    // Countdown timer for finalized retry UI
    if (hasStuckFinalized && !hasCreating) {
      setRetryCountdown(RETRY_INTERVAL);
      countdownRef.current = setInterval(() => {
        setRetryCountdown((prev) => (prev <= 1 ? RETRY_INTERVAL : prev - 1));
      }, 1000);
    }

    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    };
  }, [hasCreating, hasStuckFinalized]);

  // Auto-sync with Pennylane on mount
  const syncedRef = useRef(false);
  useEffect(() => {
    if (billing && !syncedRef.current) {
      syncedRef.current = true;
      const hasInvoices = billing.steps.some((s) => s.invoice_id);
      if (hasInvoices) {
        setSyncing(true);
        projectsApi.syncBilling(project.id)
          .then((updated) => setProject(updated))
          .catch(() => {})
          .finally(() => setSyncing(false));
      }
    }
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const updated = await projectsApi.syncBilling(project.id);
      setProject(updated);
    } catch {
      showNotification({ type: "error", message: "Erreur de synchronisation" });
    } finally {
      setSyncing(false);
    }
  };

  if (!billing) {
    return (
      <div className="text-h3 text-neutral-400 py-12 text-center">
        Aucune facturation configurée pour ce projet.
      </div>
    );
  }

  const { steps, montant_total } = billing;
  const indexed = steps.map((s, i) => ({ ...s, idx: i }));
  const s = (status: string, stepIdx?: number) => {
    const base = BASE_STATUS[status] || BASE_STATUS.pending;
    // Show retry countdown for finalized steps that have an invoice (send failed)
    if (status === "finalized" && stepIdx !== undefined) {
      const step = steps[stepIdx];
      if (step?.invoice_id) {
        const retries = retryCountRef.current[stepIdx] ?? 0;
        if (retries >= MAX_RETRIES) {
          return { label: "Envoi échoué — cliquez pour réessayer", dot: "bg-red-400" };
        }
        if (retryCountdown > 0) {
          return { label: `Envoi en attente — nouvelle tentative dans ${retryCountdown}s`, dot: "bg-amber-500 animate-pulse" };
        }
        return { label: "Envoi en attente — synchronisation...", dot: "bg-amber-500 animate-pulse" };
      }
    }
    if (status === "creating") {
      return { label: "Création et envoi en cours...", dot: "bg-amber-400 animate-pulse" };
    }
    return base;
  };

  const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex flex-row items-start justify-between py-1.5">
      <div className="text-h4 text-neutral-400">{label}</div>
      <div className="text-h4 text-neutral-700 text-right">{value}</div>
    </div>
  );

  // Guest view: only show sent/paid invoices with PDF access
  if (isGuest) {
    const visibleSteps = indexed.filter((s) => ["sent", "paid"].includes(s.status));
    if (visibleSteps.length === 0) {
      return (
        <div className="text-h3 text-neutral-400 py-12 text-center">
          Aucune facture disponible pour le moment.
        </div>
      );
    }

    // Group demarrage steps into one entry
    const guestDemarrage = visibleSteps.filter((s) => s.is_demarrage);
    const guestAvancement = visibleSteps.filter((s) => !s.is_demarrage);
    const TVA = 1.2;
    const guestDemarrageTotal = guestDemarrage.reduce((sum, s) => sum + s.montant, 0);

    const GuestStepCard = ({ label, montant, status, invoiceNumber, sentDates, sentAt, paidAt, pdfUrl }: {
      label: string; montant: number; status: string; invoiceNumber?: string;
      sentDates?: string[]; sentAt?: string; paidAt?: string; pdfUrl?: string;
    }) => (
      <div className="rounded-lg border bg-white border-neutral-200 p-4 flex flex-col gap-2">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${s(status).dot}`} />
            <div className="text-h3 font-medium text-neutral-800">{label}</div>
          </div>
          <div className="text-h3 font-medium text-neutral-700">{Math.round(montant * TVA).toLocaleString("fr-FR")} € TTC</div>
        </div>
        <div className="flex flex-col divide-y divide-neutral-50">
          <DetailRow label="Statut" value={<span className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${s(status).dot}`} />{status === "paid" ? "Payée" : "Envoyée"}</span>} />
          {invoiceNumber && <DetailRow label="N° facture" value={invoiceNumber} />}
          {sentDates && sentDates.length > 0 ? (
            sentDates.map((d, i) => (
              <DetailRow key={i} label={i === 0 ? "Envoyée le" : "Renvoyée le"} value={new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />
            ))
          ) : sentAt ? (
            <DetailRow label="Envoyée le" value={new Date(sentAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />
          ) : null}
          {paidAt && <DetailRow label="Payée le" value={new Date(paidAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />}
        </div>
        {pdfUrl && pdfUrl.startsWith("http") && (
          <button
            className="mt-1 flex items-center gap-1.5 px-3 py-1.5 text-h4 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50 w-fit"
            onClick={() => openPdf(pdfUrl, label)}
          >
            <Icon1 name="pdf" size={16} />
            Voir la facture
          </button>
        )}
      </div>
    );

    return (
      <div className="flex flex-col gap-2 w-full max-w-500">
        {guestDemarrage.length > 0 && (
          <GuestStepCard
            label="Démarrage"
            montant={guestDemarrageTotal}
            status={guestDemarrage[0].status}
            invoiceNumber={guestDemarrage[0].invoice_number}
            sentDates={guestDemarrage[0].sent_dates}
            sentAt={guestDemarrage[0].sent_at}
            paidAt={guestDemarrage[0].paid_at}
            pdfUrl={guestDemarrage[0].invoice_pdf_url}
          />
        )}
        {guestAvancement.map((step) => (
          <GuestStepCard
            key={step.idx}
            label={step.label}
            montant={step.montant}
            status={step.status}
            invoiceNumber={step.invoice_number}
            sentDates={step.sent_dates}
            sentAt={step.sent_at}
            paidAt={step.paid_at}
            pdfUrl={step.invoice_pdf_url}
          />
        ))}
        <div className="flex items-center justify-end gap-2 px-4 py-2">
          <div className="text-h3 text-neutral-400">Total TTC</div>
          <div className="text-h2 font-semibold text-neutral-800">{Math.round(montant_total * 1.2).toLocaleString("fr-FR")} €</div>
        </div>
        {pdfModal && (
          <PDFViewerModal pdfUrl={pdfModal.url} title={`Facture — ${pdfModal.title}`} onClose={() => setPdfModal(null)} />
        )}
      </div>
    );
  }

  const demarrageSteps = indexed.filter((s) => s.is_demarrage);
  const avancementSteps = indexed.filter((s) => !s.is_demarrage);
  const demarrageTotal = demarrageSteps.reduce((sum, s) => sum + s.montant, 0);
  const demarrageStatus = demarrageSteps[0]?.status || "pending";

  const act = async (fn: () => Promise<Project>, msg: string, key?: string) => {
    setLoading(true);
    if (key) setSendingKey(key);
    try {
      const updated = await fn();
      setProject(updated);
      if (msg) showNotification({ type: "success", message: msg });
      // Keep the "Envoi en cours..." visual alive while a background task
      // is still processing (status = "creating"). The polling interval
      // will clear it once the step reaches a final status.
      const stillCreating = updated.billing?.steps?.some(
        (s) => s.status === "creating",
      );
      if (!stillCreating) {
        setSendingKey(null);
      }
    } catch (e) {
      showNotification({ type: "error", message: formatError(e) });
      setSendingKey(null);
    } finally {
      setLoading(false);
    }
  };

  // Resolve controlpoint IDs to full path: theme > step > controlpoint
  const cpInfoMap = new Map<string, { cp: string; step: string; theme: string }>();
  for (const theme of project.themes || []) {
    for (const step of theme.steps) {
      for (const cp of step.controlpoints) {
        cpInfoMap.set(cp.id, { cp: cp.label, step: step.label, theme: theme.label });
      }
    }
  }

  const linkedInfos = (ids: string[]) => {
    if (!ids || ids.length === 0) return [];
    return ids.map((id) => cpInfoMap.get(id)).filter(Boolean) as { cp: string; step: string; theme: string }[];
  };

  const toggle = (key: string) => setExpandedSet((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const isExpanded = (key: string) => expandedSet.has(key);

  const StepExpanded = ({ step }: { step: ProjectBillingStep & { idx: number } }) => {
    const linked = linkedInfos(step.linked_step_ids);
    return (
      <div className="px-4 pb-3 pt-1 border-t border-neutral-100 flex flex-col">
        <div className="flex flex-col divide-y divide-neutral-50">
          <DetailRow label="Montant TTC" value={`${Math.round(step.montant * 1.2).toLocaleString("fr-FR")} €`} />
          <DetailRow label="Pourcentage" value={`${step.percentage}%`} />
          <DetailRow label="Statut" value={
            <span className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${s(step.status, step.idx).dot}`} />
              {s(step.status, step.idx).label}
            </span>
          } />
          {step.status === "sent" && step.sent_at && (() => {
            const delay = step.payment_delay_days ?? 15;
            const daysLate = Math.floor((Date.now() - new Date(step.sent_at!).getTime()) / (1000 * 60 * 60 * 24)) - delay;
            if (daysLate <= 0) return null;
            return (
              <div className="py-2 px-3 my-1 rounded-lg bg-red-50 border border-red-100">
                <div className="text-h4 text-red-700 font-medium">Retard de paiement</div>
                <div className="text-h4 text-red-600">
                  Cette facture a été envoyée il y a {daysLate + delay} jours (délai : {delay} jours). Retard de {daysLate} jour{daysLate > 1 ? "s" : ""}.
                </div>
              </div>
            );
          })()}
          {step.status === "pending" && step.project_delay_days && daysUntilEnd != null && daysUntilEnd <= step.project_delay_days && (
            <div className="py-2 px-3 my-1 rounded-lg bg-red-50 border border-red-100">
              <div className="text-h4 text-red-700 font-medium">Retard projet</div>
              <div className="text-h4 text-red-600">
                La fin du projet est prévue dans {daysUntilEnd} jour{daysUntilEnd > 1 ? "s" : ""} et cette facture n'a pas encore été créée.
              </div>
            </div>
          )}
          {step.invoice_number && <DetailRow label="N° facture" value={step.invoice_number} />}
          {linked.length > 0 && (
            <div className="py-1.5">
              <div className="text-h4 text-neutral-400 mb-1">Étape liée</div>
              {linked.map((info, i) => (
                <div key={i} className="text-h4 text-neutral-700">
                  {info.theme} › {info.step} › {info.cp}
                </div>
              ))}
            </div>
          )}
          {step.drafted_at && <DetailRow label="Brouillon créé le" value={new Date(step.drafted_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />}
          {step.finalized_at && <DetailRow label="Finalisée le" value={new Date(step.finalized_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />}
          {step.sent_dates && step.sent_dates.length > 0 ? (
            step.sent_dates.map((d, i) => (
              <DetailRow key={`sent-${i}`} label={i === 0 ? "Envoyée le" : "Renvoyée le"} value={new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />
            ))
          ) : step.sent_at ? (
            <DetailRow label="Envoyée le" value={new Date(step.sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />
          ) : null}
          {step.last_relance_sent_at && (
            <DetailRow
              label="Relance demandée le"
              value={new Date(step.last_relance_sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
            />
          )}
          {step.last_conductor_alert_sent_at && (
            <DetailRow
              label="Alerte interne envoyée le"
              value={new Date(step.last_conductor_alert_sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
            />
          )}
          {step.paid_at && <DetailRow label="Payée le" value={new Date(step.paid_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />}
        </div>

        {/* PDF button */}
        {hasPdf(step) && (
          <button
            className="mt-3 flex items-center gap-1.5 px-3 py-1.5 text-h4 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50"
            onClick={() => openPdf(step.invoice_pdf_url!, step.label)}
          >
            <Icon1 name="pdf" size={16} />
            Voir la facture
          </button>
        )}

        {/* Links + Actions */}
        <div className="flex flex-row items-center gap-2 mt-3">
          {isAdmin && step.invoice_id && (
            <a href={`https://app.pennylane.com/companies/156412/clients/customer_invoices?invoice_id=${step.invoice_id}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 text-h4 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50">
              <Icon1 name="open" size={14} />
              Pennylane
            </a>
          )}
          <div className="flex-1" />
          {step.status === "pending" && (
            <SecondaryButton variant="small" disabled={loading} label={sendingKey === `step-${step.idx}` ? "Envoi en cours..." : "Envoyer"}
              onClick={() => act(() => projectsApi.triggerBillingInvoice(project.id, step.idx), "", `step-${step.idx}`)} />
          )}
          {step.status === "draft" && (
            <SecondaryButton variant="small" disabled={loading} label={sendingKey === `step-${step.idx}` ? "Envoi en cours..." : "Finaliser et envoyer"}
              onClick={() => act(async () => {
                const p1 = await projectsApi.finalizeBillingInvoice(project.id, step.idx);
                try { return await projectsApi.sendBillingInvoice(project.id, step.idx); } catch { return p1; }
              }, "Facture finalisée et envoyée", `step-${step.idx}`)} />
          )}
          {step.status === "finalized" && (
            <SecondaryButton variant="small" disabled={loading} label={sendingKey === `step-${step.idx}` ? "Envoi en cours..." : "Envoyer au client"}
              onClick={() => act(() => projectsApi.sendBillingInvoice(project.id, step.idx), "Facture envoyée", `step-${step.idx}`)} />
          )}
          {step.status === "error" && (
            <SecondaryButton variant="small" disabled={loading} label={sendingKey === `step-${step.idx}` ? "Envoi en cours..." : "Réessayer"}
              onClick={() => act(() => projectsApi.retryBillingInvoice(project.id, step.idx), "", `step-${step.idx}`)} />
          )}
          {step.status === "sent" && canResend && (
            <button
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-h4 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-50"
              onClick={() => act(() => projectsApi.resendBillingInvoice(project.id, step.idx), "Demande de relance envoyée au comptable", `step-${step.idx}`)}
            >
              <Icon1 name="send" size={14} />
              {sendingKey === `step-${step.idx}` ? "Envoi en cours..." : "Demander une relance"}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-1 w-full max-w-500">
      {/* Démarrage group */}
      {demarrageSteps.length > 0 && (
        <div className="rounded-lg border bg-white border-neutral-200 overflow-hidden">
          <button
            className="w-full flex flex-row items-center justify-between px-4 py-2.5 hover:bg-neutral-50 transition-colors"
            onClick={() => toggle("demarrage")}
          >
            <div className="flex flex-row items-center gap-2 flex-1">
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${sendingKey === "demarrage" ? "bg-amber-400 animate-pulse" : s(demarrageStatus, demarrageSteps[0]?.idx).dot}`} />
              <div className="flex flex-col items-start">
                <div className="text-h3 font-medium text-neutral-800">Démarrage</div>
                <div className="text-h4 text-neutral-400">
                  {sendingKey === "demarrage" ? "Envoi en cours..." : s(demarrageStatus, demarrageSteps[0]?.idx).label}
                </div>
              </div>
            </div>
            <div className="flex flex-row items-center gap-3">
              <div className="text-h3 font-medium text-neutral-700">
                {Math.round(demarrageTotal * 1.2).toLocaleString("fr-FR")} €
              </div>
              <svg className={`w-4 h-4 text-neutral-400 transition-transform ${isExpanded("demarrage") ? "rotate-180" : ""}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          {isExpanded("demarrage") && (
            <div className="px-4 pb-3 pt-1 border-t border-neutral-100 flex flex-col">
              <div className="flex flex-col divide-y divide-neutral-50">
                <DetailRow label="Montant TTC" value={`${Math.round(demarrageTotal * 1.2).toLocaleString("fr-FR")} €`} />
                <DetailRow label="Statut" value={
                  <span className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${s(demarrageStatus, demarrageSteps[0]?.idx).dot}`} />
                    {s(demarrageStatus, demarrageSteps[0]?.idx).label}
                  </span>
                } />
                {demarrageSteps[0]?.invoice_number && <DetailRow label="N° facture" value={demarrageSteps[0].invoice_number} />}
                {demarrageSteps.map((st, i) => (
                  <DetailRow key={i} label={st.label} value={`${Math.round(st.montant * 1.2).toLocaleString("fr-FR")} € (${st.percentage}%)`} />
                ))}
                {demarrageSteps[0]?.drafted_at && <DetailRow label="Brouillon créé le" value={new Date(demarrageSteps[0].drafted_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />}
                {demarrageSteps[0]?.finalized_at && <DetailRow label="Finalisée le" value={new Date(demarrageSteps[0].finalized_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />}
                {demarrageSteps[0]?.sent_dates && demarrageSteps[0].sent_dates.length > 0 ? (
                  demarrageSteps[0].sent_dates.map((d, i) => (
                    <DetailRow key={`sent-${i}`} label={i === 0 ? "Envoyée le" : "Renvoyée le"} value={new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />
                  ))
                ) : demarrageSteps[0]?.sent_at ? (
                  <DetailRow label="Envoyée le" value={new Date(demarrageSteps[0].sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />
                ) : null}
                {demarrageSteps[0]?.last_relance_sent_at && (
                  <DetailRow
                    label="Relance demandée le"
                    value={new Date(demarrageSteps[0].last_relance_sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
                  />
                )}
                {demarrageSteps[0]?.last_conductor_alert_sent_at && (
                  <DetailRow
                    label="Alerte interne envoyée le"
                    value={new Date(demarrageSteps[0].last_conductor_alert_sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
                  />
                )}
                {demarrageSteps[0]?.paid_at && <DetailRow label="Payée le" value={new Date(demarrageSteps[0].paid_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })} />}
              </div>
              {demarrageSteps[0] && hasPdf(demarrageSteps[0]) && (
                <button
                  className="mt-3 flex items-center gap-1.5 px-3 py-1.5 text-h4 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50"
                  onClick={() => openPdf(demarrageSteps[0].invoice_pdf_url!, "Démarrage")}
                >
                  <Icon1 name="pdf" size={16} />
                  Voir la facture
                </button>
              )}
              <div className="flex flex-row items-center gap-2 mt-3">
                {isAdmin && demarrageSteps[0]?.invoice_id && (
                  <a href={`https://app.pennylane.com/companies/156412/clients/customer_invoices?invoice_id=${demarrageSteps[0].invoice_id}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-h4 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50">
                    <Icon1 name="open" size={14} />
                    Pennylane
                  </a>
                )}
                <div className="flex-1" />
                {demarrageStatus === "pending" && (
                  <SecondaryButton variant="small" disabled={loading} label={sendingKey === "demarrage" ? "Envoi en cours..." : "Envoyer"}
                    onClick={() => act(() => projectsApi.triggerDemarrageInvoice(project.id), "", "demarrage")} />
                )}
                {demarrageStatus === "error" && (
                  <SecondaryButton variant="small" disabled={loading} label={sendingKey === "demarrage" ? "Envoi en cours..." : "Réessayer"}
                    onClick={() => act(() => projectsApi.triggerDemarrageInvoice(project.id), "", "demarrage")} />
                )}
                {demarrageStatus === "draft" && (
                  <SecondaryButton variant="small" disabled={loading} label={sendingKey === "demarrage" ? "Envoi en cours..." : "Finaliser et envoyer"}
                    onClick={() => act(async () => {
                      const p1 = await projectsApi.finalizeBillingInvoice(project.id, demarrageSteps[0].idx);
                      try { return await projectsApi.sendBillingInvoice(project.id, demarrageSteps[0].idx); } catch { return p1; }
                    }, "Facture finalisée et envoyée", "demarrage")} />
                )}
                {demarrageStatus === "finalized" && (
                  <SecondaryButton variant="small" disabled={loading} label={sendingKey === "demarrage" ? "Envoi en cours..." : "Envoyer au client"}
                    onClick={() => act(() => projectsApi.sendBillingInvoice(project.id, demarrageSteps[0].idx), "Facture envoyée", "demarrage")} />
                )}
                {demarrageStatus === "sent" && canResend && (
                  <button
                    disabled={loading}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-h4 rounded-lg border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-50"
                    onClick={() => act(() => projectsApi.resendBillingInvoice(project.id, demarrageSteps[0].idx), "Demande de relance envoyée au comptable", "demarrage")}
                  >
                    <Icon1 name="send" size={14} />
                    {sendingKey === "demarrage" ? "Envoi en cours..." : "Demander une relance"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Avancement steps */}
      {avancementSteps.map((step) => (
        <div key={step.idx} className="rounded-lg border bg-white border-neutral-200 overflow-hidden">
          <button
            className="w-full flex flex-row items-center justify-between px-4 py-2.5 hover:bg-neutral-50 transition-colors"
            onClick={() => toggle(`step-${step.idx}`)}
          >
            <div className="flex flex-row items-center gap-2 flex-1">
              {(() => {
                const isProjectDelay = step.status === "pending" && step.project_delay_days && daysUntilEnd != null && daysUntilEnd <= step.project_delay_days;
                const isLatePayment = step.status === "sent" && step.sent_at && (() => {
                  const delay = step.payment_delay_days ?? 15;
                  return (Date.now() - new Date(step.sent_at!).getTime()) / (1000 * 60 * 60 * 24) > delay;
                })();
                // Step gap: find previous step's sent_at
                const isStepGap = step.step_gap_delay_days && ["pending", "draft", "finalized"].includes(step.status) && (() => {
                  const myIndex = avancementSteps.findIndex((s) => s.idx === step.idx);
                  if (myIndex <= 0) return false;
                  const prev = avancementSteps[myIndex - 1];
                  if (!prev.sent_at || !["sent", "paid"].includes(prev.status)) return false;
                  const daysSincePrev = (Date.now() - new Date(prev.sent_at).getTime()) / (1000 * 60 * 60 * 24);
                  return daysSincePrev > step.step_gap_delay_days!;
                })();
                const isRetard = isProjectDelay || isLatePayment || isStepGap;
                const isSending = sendingKey === `step-${step.idx}`;
                return <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isSending ? "bg-amber-400 animate-pulse" : isRetard ? "bg-red-500" : s(step.status, step.idx).dot}`} />;
              })()}
              <div className="flex flex-col items-start">
                <div className="text-h3 font-medium text-neutral-800">{step.label}</div>
                <div className="text-h4 text-neutral-400">
                  {sendingKey === `step-${step.idx}` ? `${step.percentage}% · Envoi en cours...` : `${step.percentage}% · ${s(step.status, step.idx).label}`}
                </div>
                {step.status === "sent" && step.sent_at && (() => {
                  const delay = step.payment_delay_days ?? 15;
                  const daysLate = Math.floor((Date.now() - new Date(step.sent_at!).getTime()) / (1000 * 60 * 60 * 24)) - delay;
                  if (daysLate <= 0) return null;
                  return (
                    <div className="text-h5 text-red-600 font-medium">
                      Retard de paiement — {daysLate} jour{daysLate > 1 ? "s" : ""} de retard
                    </div>
                  );
                })()}
                {step.step_gap_delay_days && ["pending", "draft", "finalized"].includes(step.status) && (() => {
                  const myIndex = avancementSteps.findIndex((s) => s.idx === step.idx);
                  if (myIndex <= 0) return null;
                  const prev = avancementSteps[myIndex - 1];
                  if (!prev.sent_at || !["sent", "paid"].includes(prev.status)) return null;
                  const daysSincePrev = Math.floor((Date.now() - new Date(prev.sent_at).getTime()) / (1000 * 60 * 60 * 24));
                  const daysLate = daysSincePrev - step.step_gap_delay_days!;
                  if (daysLate <= 0) return null;
                  return (
                    <div className="text-h5 text-red-600 font-medium">
                      Retard entre étapes — {daysLate} jour{daysLate > 1 ? "s" : ""} après l'étape précédente
                    </div>
                  );
                })()}
                {step.status === "pending" && step.project_delay_days && daysUntilEnd != null && daysUntilEnd <= step.project_delay_days && (
                  <div className="text-h5 text-red-600 font-medium">
                    Retard projet — fin dans {daysUntilEnd} jour{daysUntilEnd > 1 ? "s" : ""}, facture non créée
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-row items-center gap-3">
              <div className="text-h3 font-medium text-neutral-700">
                {Math.round(step.montant * 1.2).toLocaleString("fr-FR")} €
              </div>
              <svg className={`w-4 h-4 text-neutral-400 transition-transform ${isExpanded(`step-${step.idx}`) ? "rotate-180" : ""}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          {isExpanded(`step-${step.idx}`) && <StepExpanded step={step} />}
        </div>
      ))}

      {/* Footer: sync + total */}
      <div className="flex flex-row items-center justify-between px-4 py-2 mt-1">
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center gap-1.5 text-h4 text-neutral-400 hover:text-neutral-600 disabled:opacity-50"
        >
          <Icon1 name="refresh" size={14} />
          {syncing ? "Synchronisation..." : "Synchroniser"}
        </button>
        <div className="flex items-center gap-2">
          <div className="text-h3 text-neutral-400">Total TTC</div>
          <div className="text-h2 font-semibold text-neutral-800">
            {Math.round(montant_total * 1.2).toLocaleString("fr-FR")} €
          </div>
        </div>
      </div>

      {pdfModal && (
        <PDFViewerModal
          pdfUrl={pdfModal.url}
          title={`Facture — ${pdfModal.title}`}
          onClose={() => setPdfModal(null)}
        />
      )}
    </div>
  );
}
